import React, { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Contact.css';

const Contact = () => {
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      const publicApi = axios.create({
        // baseURL: 'https://pranayuvbackendfinal-production.up.railway.app',
        baseURL: 'http://localhost:8080/',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      await publicApi.post('/pran/contact', {
        email,
        phone_number: phoneNumber,
        message,
      });
      setSuccess('Message sent successfully! We will get back to you soon.');
      setEmail('');
      setPhoneNumber('');
      setMessage('');
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Contact endpoint not found. Please contact support.');
      } else {
        setError(`Failed to send message: ${error.response?.status || ''} ${error.response?.data?.message || error.message}`);
      }
      console.error('Contact error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'bi-geo-alt-fill',
      title: 'Our Location',
      content: 'Vijayawada, Andhra Pradesh, India',
      link: 'https://maps.app.goo.gl/sgFxR1QFra4ASKkv5',
      gradient: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)'
    },
    {
      icon: 'bi-envelope-fill',
      title: 'Email Us',
      content: 'contact@pranayuv.com',
      link: 'mailto:contact@pranayuv.com',
      gradient: 'linear-gradient(135deg, #4ECDC4, #44A08D)'
    },
    {
      icon: 'bi-telephone-fill',
      title: 'Call Us',
      content: '+91 9676448550',
      link: 'tel:+919676448550',
      gradient: 'linear-gradient(135deg, #667eea, #764ba2)'
    }
  ];

  const socialLinks = [
    { icon: 'bi-whatsapp', url: 'https://wa.me/919676448550', color: '#25D366' },
    { icon: 'bi-instagram', url: 'https://www.instagram.com/team_pranayuv/', color: '#E4405F' },
    { icon: 'bi-linkedin', url: 'https://www.linkedin.com/company/pranayuv-technologies/', color: '#0077B5' }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative py-5">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8 mx-auto text-center text-white" data-aos="fade-up">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="display-2 fw-bold mb-4 hero-title">
                  Get <span className="text-gradient">In Touch</span>
                </h1>
                <p className="lead mb-5 hero-subtitle">
                  Ready to transform your ideas into reality? We'd love to hear from you. 
                  Let's create something amazing together.
                </p>
                <div className="hero-stats row text-center">
                  <div className="col-md-4 mb-3">
                    <div className="stat-number">24/7</div>
                    <div className="stat-label">Support</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="stat-number">2000+</div>
                    <div className="stat-label">Hours of Research</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="stat-number">5★</div>
                    <div className="stat-label">Rating</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="hero-waves">
          <svg viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="rgba(255,255,255,0.1)" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,112C960,139,1056,181,1152,186.7C1248,192,1344,160,1392,144L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-5 contact-info-section">
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-lg-8 text-center" data-aos="fade-up">
              <h2 className="display-5 fw-bold mb-3">Let's Connect</h2>
              <p className="lead text-muted">Choose your preferred way to reach out to us</p>
            </div>
          </div>
          <div className="row g-4">
            {contactInfo.map((info, index) => (
              <div key={index} className="col-lg-4" data-aos="fade-up" data-aos-delay={index * 100}>
                <motion.div 
                  className="contact-card h-100"
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="contact-card-inner">
                    <div className="contact-icon-wrapper" style={{ background: info.gradient }}>
                      <i className={`bi ${info.icon}`}></i>
                    </div>
                    <h3 className="contact-card-title">{info.title}</h3>
                    <a 
                      href={info.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-card-link"
                    >
                      {info.content}
                    </a>
                    <div className="contact-card-glow" style={{ background: info.gradient }}></div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-5 main-contact-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xxl-10">
              <div className="row g-5 align-items-center">
                {/* Contact Form */}
                <div className="col-lg-8" data-aos="fade-right">
                  <motion.div 
                    className="contact-form-wrapper"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="contact-form-header">
                      <h2 className="display-6 fw-bold mb-2">Send us a Message</h2>
                      <p className="text-muted mb-4">We'll get back to you within 24 hours</p>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="contact-form">
                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="email"
                              id="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="form-control form-control-modern"
                              placeholder="Enter your email"
                              required
                            />
                            <label htmlFor="email">
                              <i className="bi bi-envelope me-2"></i>Email Address
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              type="tel"
                              id="phoneNumber"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                              className="form-control form-control-modern"
                              placeholder="Enter your phone number"
                            />
                            <label htmlFor="phoneNumber">
                              <i className="bi bi-telephone me-2"></i>Phone Number
                            </label>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="form-floating">
                            <textarea
                              id="message"
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              className="form-control form-control-modern"
                              style={{ height: '150px' }}
                              placeholder="Type your message here..."
                              required
                            ></textarea>
                            <label htmlFor="message">
                              <i className="bi bi-chat-left-text me-2"></i>Your Message
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <motion.button 
                        type="submit" 
                        className="btn btn-primary btn-lg btn-modern w-100 mt-4"
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSubmitting ? (
                          <Fragment>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending Message...
                          </Fragment>
                        ) : (
                          <Fragment>
                            <i className="bi bi-send me-2"></i>
                            Send Message
                          </Fragment>
                        )}
                      </motion.button>

                      {success && (
                        <motion.div 
                          className="alert alert-success mt-4 mb-0 modern-alert"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <i className="bi bi-check-circle-fill me-2"></i>
                          {success}
                        </motion.div>
                      )}
                      {error && (
                        <motion.div 
                          className="alert alert-danger mt-4 mb-0 modern-alert"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          {error}
                        </motion.div>
                      )}
                    </form>
                  </motion.div>
                </div>

                {/* Right Side Content */}
                <div className="col-lg-4" data-aos="fade-left">
                  {/* Map */}
                  <motion.div 
                    className="map-wrapper mb-4"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.807993213446!2d80.68995417460827!3d16.485257228167384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a35fbe9829a446d%3A0x3f335a0eaf42794b!2sPRANAYUV%20TECHNOLOGIES!5e0!3m2!1sen!2sin!4v1749192844474!5m2!1sen!2sin"
                      width="100%"
                      height="250"
                      style={{ border: 0, borderRadius: '20px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps showing PRANAYUV TECHNOLOGIES in Vijayawada"
                    ></iframe>
                  </motion.div>

                  {/* Social Links */}
                  <motion.div 
                    className="social-section"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <h4 className="fw-bold mb-4 text-center">Follow Us</h4>
                    <div className="d-flex justify-content-center gap-3">
                      {socialLinks.map((social, index) => (
                        <motion.a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-btn"
                          style={{ '--social-color': social.color }}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <i className={`bi ${social.icon}`}></i>
                        </motion.a>
                      ))}
                    </div>
                  </motion.div>

                  {/* Additional Info */}
                  <motion.div 
                    className="additional-info mt-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <div className="info-card">
                      <h5 className="fw-bold mb-3">
                        <i className="bi bi-clock text-primary me-2"></i>
                        Business Hours
                      </h5>
                      <div className="time-slot">
                        <span>Monday - Friday</span>
                        <span>9:00 AM - 6:00 PM</span>
                      </div>
                      <div className="time-slot">
                        <span>Saturday</span>
                        <span>9:00 AM - 6:00 PM</span>
                      </div>
                      <div className="time-slot">
                        <span>Sunday</span>
                        <span>Closed</span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;